/**
 * VoiceMode — Full-screen overlay for real-time voice conversation with Alex
 */

import { useEffect, useRef, useState } from 'react';
import { useGeminiLive, TranscriptEntry, ChatMessage } from '../../hooks/useGeminiLive';
import { useLanguage } from '../../contexts/LanguageContext';

interface VoiceModeProps {
  onClose: (transcript: TranscriptEntry[]) => void;
  chatHistory?: ChatMessage[];
}

export function VoiceMode({ onClose, chatHistory }: VoiceModeProps) {
  const { t, language } = useLanguage();
  const {
    state, connect, disconnect, reconnect, setSilenceDuration,
    transcript, error,
    micLevel, rawMicLevel, audioChunksSent, debugInfo,
    micDeviceName, availableDevices, refreshDevices,
  } = useGeminiLive(language, chatHistory);
  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [showDebug, setShowDebug] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Pause detection sensitivity — persisted to localStorage
  const SILENCE_STORAGE_KEY = 'postmortem-voice-silence-ms';
  const [silenceDurationMs, setSilenceDurationMs] = useState(() => {
    const stored = localStorage.getItem(SILENCE_STORAGE_KEY);
    return stored ? Number(stored) : 1500;
  });

  // Load available devices on mount
  useEffect(() => {
    refreshDevices();
  }, [refreshDevices]);

  // Session timer — starts when active, keeps counting during reconnects (connecting)
  useEffect(() => {
    if (state === 'active' && !timerRef.current) {
      // Start timer if not already running
      timerRef.current = setInterval(() => {
        setElapsed(prev => prev + 1);
      }, 1000);
    } else if (state === 'connecting' && timerRef.current) {
      // Keep timer running during reconnect (don't reset)
    } else if (state !== 'active' && state !== 'connecting') {
      // Stop and reset on idle/error
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setElapsed(0);
    }
    return () => {
      if (timerRef.current && state !== 'active' && state !== 'connecting') {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [state]);

  // Auto-scroll transcript
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [transcript]);

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // No hard limit with compression enabled — timer is informational only

  const handleClose = async () => {
    await disconnect();
    onClose(transcript);
  };

  const handleConnect = () => {
    setSilenceDuration(silenceDurationMs);
    connect(selectedDeviceId || undefined);
  };

  const handleSilenceDurationChange = (newMs: number) => {
    setSilenceDurationMs(newMs);
    localStorage.setItem(SILENCE_STORAGE_KEY, String(newMs));
    setSilenceDuration(newMs);

    // If active, debounce reconnect to apply new setting
    if (state === 'active') {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = setTimeout(() => {
        reconnect();
        reconnectTimerRef.current = null;
      }, 1000);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'var(--surface)',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
    }}>
      <style>{`
        @keyframes voicePulse {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.15); opacity: 1; }
        }
        @keyframes voiceRing {
          0% { transform: scale(1); opacity: 0.4; }
          100% { transform: scale(2); opacity: 0; }
        }
      `}</style>

      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '16px' }}>
            {t('voice.voiceMode')}
          </h3>
          {(state === 'active' || (state === 'connecting' && elapsed > 0)) && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              {/* Pulsing dot to indicate active session */}
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: state === 'connecting' ? '#eab308' : '#22c55e',
                animation: 'voicePulse 2s ease-in-out infinite',
              }} />
              <span style={{
                fontSize: '13px',
                fontFamily: 'monospace',
                fontWeight: '600',
                color: 'var(--text-muted)',
              }}>
                {formatTime(elapsed)}
              </span>
              {state === 'connecting' && elapsed > 0 && (
                <span style={{ fontSize: '11px', color: '#eab308' }}>
                  {t('voice.reconnecting')}
                </span>
              )}
            </div>
          )}
        </div>
        <button
          onClick={handleClose}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--surface-muted)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          {t('common.close')}
        </button>
      </div>

      {/* Main content */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        gap: '24px',
        overflow: 'hidden',
      }}>

        {/* Device selection — shown before connecting */}
        {state === 'idle' && (
          <div style={{
            width: '100%',
            maxWidth: '400px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            alignItems: 'center',
          }}>
            <div style={{ fontSize: '18px', fontWeight: '600', textAlign: 'center' }}>
              {t('voice.selectMicrophone')}
            </div>

            {availableDevices.length > 0 ? (
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {availableDevices.map((device) => (
                  <button
                    key={device.deviceId}
                    onClick={() => setSelectedDeviceId(device.deviceId)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      textAlign: 'left',
                      backgroundColor: selectedDeviceId === device.deviceId ? 'var(--primary-soft, rgba(79,70,229,0.1))' : 'var(--surface-muted)',
                      border: selectedDeviceId === device.deviceId ? '2px solid var(--primary)' : '1px solid var(--border)',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      color: 'var(--text)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    <span style={{ fontSize: '18px' }}>
                      {selectedDeviceId === device.deviceId ? '\u25C9' : '\u25CB'}
                    </span>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {device.label}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                {t('voice.loadingDevices')}
              </div>
            )}

            {/* Pause detection sensitivity slider */}
            <div style={{ width: '100%', padding: '8px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: '500' }}>
                  {t('voice.pauseSensitivity')}
                </span>
                <span style={{ fontSize: '11px', fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                  {(silenceDurationMs / 1000).toFixed(1)}s
                </span>
              </div>
              <input
                type="range"
                min={500}
                max={3000}
                step={100}
                value={silenceDurationMs}
                onChange={(e) => handleSilenceDurationChange(Number(e.target.value))}
                aria-label={t('voice.pauseSensitivity')}
                style={{ width: '100%', accentColor: 'var(--primary)' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                <span>{t('voice.responsive')}</span>
                <span>{t('voice.patient')}</span>
              </div>
            </div>

            <button
              onClick={handleConnect}
              disabled={availableDevices.length === 0}
              style={{
                padding: '14px 32px',
                backgroundColor: availableDevices.length === 0 ? 'var(--border)' : 'var(--primary)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: availableDevices.length === 0 ? 'not-allowed' : 'pointer',
                marginTop: '8px',
              }}
            >
              {selectedDeviceId ? t('voice.startWithSelectedMic') : t('voice.startWithDefaultMic')}
            </button>
          </div>
        )}

        {/* Connecting spinner */}
        {state === 'connecting' && (
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            border: '3px solid var(--border)',
            borderTopColor: 'var(--primary)',
            animation: 'protectedRouteSpinner 0.8s linear infinite',
          }} />
        )}

        {/* Active — pulsing indicator */}
        {state === 'active' && (
          <div style={{ position: 'relative' as const, width: '100px', height: '100px' }}>
            <div style={{
              position: 'absolute' as const,
              inset: '20px',
              borderRadius: '50%',
              backgroundColor: 'var(--primary)',
              animation: 'voicePulse 2s ease-in-out infinite',
            }} />
            <div style={{
              position: 'absolute' as const,
              inset: '20px',
              borderRadius: '50%',
              border: '2px solid var(--primary)',
              animation: 'voiceRing 2s ease-out infinite',
            }} />
          </div>
        )}

        {/* Error */}
        {state === 'error' && (
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: 'var(--danger-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '32px',
          }}>
            !
          </div>
        )}

        {/* Status text */}
        <div style={{
          fontSize: '16px',
          color: 'var(--text-muted)',
          textAlign: 'center',
        }}>
          {state === 'connecting' && t('voice.connecting')}
          {state === 'active' && t('voice.listening')}
          {state === 'error' && (error || t('voice.voiceError'))}
        </div>

        {state === 'error' && (
          <button
            onClick={handleConnect}
            style={{
              padding: '10px 20px',
              backgroundColor: 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            {t('common.retry')}
          </button>
        )}

        {/* Live Mic Level Meter — only when active */}
        {state === 'active' && (
          <div style={{
            width: '100%',
            maxWidth: '400px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          }}>
            {/* Single level bar (raw mic) */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)', minWidth: '30px' }}>{t('voice.mic')}</span>
              <div style={{
                flex: 1,
                height: '16px',
                backgroundColor: 'var(--surface-muted)',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '1px solid var(--border)',
              }}>
                <div style={{
                  width: `${rawMicLevel}%`,
                  height: '100%',
                  borderRadius: '8px',
                  backgroundColor: rawMicLevel > 30 ? '#22c55e' : rawMicLevel > 10 ? '#eab308' : '#ef4444',
                  transition: 'width 0.1s linear',
                }} />
              </div>
              <span style={{ fontSize: '11px', fontFamily: 'monospace', color: 'var(--text-muted)', minWidth: '35px', textAlign: 'right' }}>
                {rawMicLevel}%
              </span>
            </div>

            {/* Multi-bar visualizer */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              gap: '2px',
              height: '40px',
            }}>
              {Array.from({ length: 20 }, (_, i) => {
                const threshold = (i + 1) * 5;
                const active = rawMicLevel >= threshold;
                return (
                  <div key={i} style={{
                    width: '12px',
                    height: `${20 + i * 1}px`,
                    borderRadius: '2px',
                    backgroundColor: active
                      ? (threshold > 60 ? '#22c55e' : threshold > 30 ? '#eab308' : '#ef4444')
                      : 'var(--border)',
                    transition: 'background-color 0.05s',
                  }} />
                );
              })}
            </div>

            {/* Device name + status */}
            <div style={{
              fontSize: '11px',
              fontFamily: 'monospace',
              color: 'var(--text-muted)',
              textAlign: 'center',
              padding: '4px',
              lineHeight: '1.6',
            }}>
              {micDeviceName && (
                <div style={{ fontSize: '10px', opacity: 0.8 }}>
                  {micDeviceName}
                </div>
              )}
              {rawMicLevel === 0 && audioChunksSent > 10 && (
                <div style={{ color: '#ef4444', marginTop: '4px' }}>
                  {t('voice.noMicInput')}
                </div>
              )}
            </div>

            {/* Pause detection slider (compact) */}
            <div style={{ width: '100%', padding: '4px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                  {t('voice.pauseSensitivity')}
                </span>
                <span style={{ fontSize: '10px', fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                  {(silenceDurationMs / 1000).toFixed(1)}s
                </span>
              </div>
              <input
                type="range"
                min={500}
                max={3000}
                step={100}
                value={silenceDurationMs}
                onChange={(e) => handleSilenceDurationChange(Number(e.target.value))}
                aria-label={t('voice.pauseSensitivity')}
                style={{ width: '100%', accentColor: 'var(--primary)', height: '16px' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: 'var(--text-muted)' }}>
                <span>{t('voice.responsive')}</span>
                <span>{t('voice.patient')}</span>
              </div>
            </div>

            {/* Debug toggle */}
            <button
              onClick={() => setShowDebug(!showDebug)}
              style={{
                alignSelf: 'center',
                padding: '2px 10px',
                fontSize: '10px',
                color: 'var(--text-muted)',
                backgroundColor: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                cursor: 'pointer',
                opacity: 0.6,
              }}
            >
              {showDebug ? t('voice.hideDebug') : t('voice.debug')}
            </button>

            {showDebug && (
              <div style={{
                fontSize: '10px',
                fontFamily: 'monospace',
                color: 'var(--text-muted)',
                textAlign: 'center',
                padding: '4px',
                lineHeight: '1.8',
                backgroundColor: 'var(--surface-muted)',
                borderRadius: '6px',
              }}>
                <div>Chunks: {audioChunksSent} | Raw: {rawMicLevel}% | Worklet: {micLevel}%</div>
                {debugInfo && <div>{debugInfo}</div>}
              </div>
            )}
          </div>
        )}

        {/* Transcript */}
        {transcript.length > 0 && (
          <div style={{
            width: '100%',
            maxWidth: '600px',
            flex: 1,
            overflowY: 'auto',
            padding: '12px',
            borderRadius: '12px',
            backgroundColor: 'var(--surface-muted)',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}>
            <div style={{
              fontSize: '12px',
              color: 'var(--text-muted)',
              fontWeight: '600',
              marginBottom: '4px',
            }}>
              {t('voice.transcript')}
            </div>
            {transcript.map((entry, idx) => (
              <div key={idx} style={{
                fontSize: '14px',
                color: 'var(--text)',
                padding: '6px 10px',
                borderRadius: '8px',
                backgroundColor: entry.role === 'user' ? 'var(--primary-soft, rgba(79,70,229,0.1))' : 'var(--surface)',
                alignSelf: entry.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
              }}>
                <span style={{ fontWeight: '600', fontSize: '11px', color: 'var(--text-muted)' }}>
                  {entry.role === 'user' ? t('weeklyCheckin.you') : t('weeklyCheckin.alex')}:
                </span>{' '}
                {entry.text}
              </div>
            ))}
            <div ref={transcriptEndRef} />
          </div>
        )}
      </div>

      {/* Bottom controls */}
      {state === 'active' && (
        <div style={{
          padding: '20px',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'center',
        }}>
          <button
            onClick={handleClose}
            style={{
              padding: '14px 32px',
              backgroundColor: 'var(--danger)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            {t('voice.endVoice')}
          </button>
        </div>
      )}
    </div>
  );
}
